import React, { useState, useEffect, useCallback } from "react";
import { PageLayout, LoadingSpinner } from "lib";
import { EmailLogsAPI } from "api_inbusiness";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useUser } from "hooks/useUser";
import { useHistory } from "react-router-dom";

// MUI components
import {
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Pagination,
} from "@mui/material";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import styles from "./EmailLogs.module.scss";

// Category labels and colors
const CATEGORY_CONFIG = {
  SUBSCRIPTION_RENEWAL: { label: "Reînnoire Abonament", color: "#4A90E2" },
  SUBSCRIPTION_EXPIRY: { label: "Expirare Abonament", color: "#F39C12" },
  EVENT_TICKET: { label: "Ticket Eveniment", color: "#9B59B6" },
  EVENT_EMAIL: { label: "Email Eveniment", color: "#27AE60" },
  WELCOME: { label: "Bun Venit", color: "#1ABC9C" },
  PASSWORD_RESET: { label: "Resetare Parolă", color: "#E74C3C" },
  OTHER: { label: "Altele", color: "#95A5A6" },
};

// Stats Card Component
const StatCard = ({ icon, label, value, color, subtext }) => (
  <div className={styles.statCard} style={{ borderColor: color }}>
    <div className={styles.statIcon} style={{ backgroundColor: `${color}20`, color }}>
      {icon}
    </div>
    <div className={styles.statInfo}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      {subtext && <span className={styles.statSubtext}>{subtext}</span>}
    </div>
  </div>
);

// Email Log Row Component
const EmailLogRow = ({ log }) => {
  const categoryConfig = CATEGORY_CONFIG[log.category] || CATEGORY_CONFIG.OTHER;
  const isFailed = log.status === "FAILED";
  const userName = log.user 
    ? `${log.user.firstName} ${log.user.lastName}` 
    : "-";

  return (
    <div className={`${styles.logRow} ${isFailed ? styles.failed : ""}`}>
      <div className={styles.statusIcon}>
        {isFailed ? (
          <ErrorIcon sx={{ color: "#E74C3C" }} />
        ) : (
          <CheckCircleIcon sx={{ color: "#27AE60" }} />
        )}
      </div>
      <div className={styles.logMain}>
        <div className={styles.logRecipient}>{log.recipient}</div>
        <div className={styles.logSubject}>{log.subject}</div>
      </div>
      <div className={styles.logUser}>
        <span>{userName}</span>
      </div>
      <div className={styles.logDate}>
        {dayjs(log.sentAt).format("DD MMM YYYY, HH:mm")}
      </div>
      <div className={styles.logCategory}>
        <Chip
          label={categoryConfig.label}
          size="small"
          sx={{
            backgroundColor: `${categoryConfig.color}20`,
            color: categoryConfig.color,
            fontSize: "0.7rem",
            height: "24px",
            minWidth: "100px",
          }}
        />
      </div>
    </div>
  );
};


// Main Component
const EmailLogs = () => {
  const { permissionsData } = useUser();
  const navigate = useHistory();

  // Redirect non-admin users to home
  useEffect(() => {
    if (!permissionsData.seeAdminButtons) {
      navigate.push("/");
    }
  }, [permissionsData.seeAdminButtons, navigate]);

  // State
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Filters
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  // Fetch stats
  const fetchStats = useCallback(async () => {
    const res = await EmailLogsAPI.getStats();
    if (res.ok) {
      setStats(res.data);
    }
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const res = await EmailLogsAPI.getLogs({
      page,
      limit,
      search: search || undefined,
      category: category || undefined,
      status: status || undefined,
    });
    if (res.ok) {
      setLogs(res.data.content || []);
      setTotal(res.data.total || 0);
    } else {
      toast.error("Eroare la încărcarea email-urilor");
    }
    setLoading(false);
  }, [page, limit, search, category, status]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, category, status]);

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
  };

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setStatus("");
    setPage(1);
  };

  const hasActiveFilters = search || category || status;
  const totalPages = Math.ceil(total / limit);

  return (
    <PageLayout title="Istoric Email-uri">
      <div className={styles.container}>
        {/* Stats Section */}
        <div className={styles.statsSection}>
          <StatCard
            icon={<EmailIcon />}
            label="Total Trimise"
            value={stats?.total || 0}
            color="#4A90E2"
          />
          <StatCard
            icon={<CheckCircleIcon />}
            label="Succes"
            value={stats?.totalSent || 0}
            color="#27AE60"
            subtext={stats?.total ? `${Math.round((stats.totalSent / stats.total) * 100)}%` : "0%"}
          />
          <StatCard
            icon={<ErrorIcon />}
            label="Eșuate"
            value={stats?.totalFailed || 0}
            color="#E74C3C"
          />
          <StatCard
            icon={<TrendingUpIcon />}
            label="Ultimele 7 zile"
            value={stats?.recentActivity || 0}
            color="#9B59B6"
          />
        </div>

        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <TextField
            placeholder="Caută după email sau subiect..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            className={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: "400px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Categorie</InputLabel>
            <Select
              value={category}
              label="Categorie"
              onChange={(e) => setCategory(e.target.value)}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="">Toate</MenuItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="">Toate</MenuItem>
              <MenuItem value="SENT">Trimis</MenuItem>
              <MenuItem value="FAILED">Eșuat</MenuItem>
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Tooltip title="Șterge filtrele">
              <IconButton onClick={clearFilters} className={styles.clearBtn}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Reîncarcă">
            <IconButton onClick={handleRefresh} className={styles.refreshBtn}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>

        {/* Logs Table */}
        <div className={styles.logsSection}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner loading={true} />
            </div>
          ) : logs.length === 0 ? (
            <div className={styles.emptyState}>
              <EmailIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
              <p>Nu există email-uri înregistrate</p>
            </div>
          ) : (
            <>
              <div className={styles.logsHeader}>
                <span>Afișare {logs.length} din {total} rezultate</span>
              </div>
              <div className={styles.logsList}>
                {logs.map((log) => (
                  <EmailLogRow key={log.id} log={log} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, newPage) => setPage(newPage)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default EmailLogs;
